import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
  TooltipFormatter,
} from "recharts";

import { IWithballData } from "types";
import {
  calculatePecentage,
  summariseShoot,
  buildMatrixFromShootData,
} from "utils";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";

interface IShootPannel {
  dataArray: IWithballData[];
}

const ASPECT = 2;
const CHART_WIDTH = "80%";
const BAR_SIZE = 20;

export const ShootPannel = ({ dataArray }: IShootPannel) => {
  const { summary, matrix } = useMemo(() => {
    return {
      summary: summariseShoot(dataArray),
      matrix: buildMatrixFromShootData(dataArray),
    };
  }, [dataArray]);

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    let label = "";
    switch (value) {
      case "goal":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL");
        break;
      case "intarget":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET");
        break;
      case "total":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_TOTAL");
        break;
    }
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const tooltipFormatter: TooltipFormatter = (value, name) => {
    let label = "";
    switch (name) {
      case "goal":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL");
        break;
      case "intarget":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET");
        break;
      case "total":
        label = intl.get("MATCH_TEAM_OVERVIEW_SHOOT_TOTAL");
        break;
    }
    return [value, label];
  };

  const assistLegendFormatter: LegendValueFormatter = (value, entry) => {
    let label = intl.get("MATCH_TEAM_OVERVIEW_ASSIST_TOTAL");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const renderShootChart = () => {
    const data = Object.getOwnPropertyNames(matrix.shoots).map((property) => {
      return {
        name: matrix.shoots[property][0].fromName,
        total: matrix.shoots[property].length,
        goal: matrix.shoots[property].filter((data) => data.succeed).length,
        intarget: matrix.shoots[property].filter((data) => data.threat).length,
      };
    });
    if (data.length <= 0) {
      return null;
    }
    return (
      <Grid item xs={12} md={6} lg={6}>
        <Typography variant="h4">
          {intl.get("MATCH_TEAM_OVERVIEW_SHOOT_CHART_TITLE")}
          <Box mt={2} />
          <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis dataKey="name" angle={-45} interval={0} />
              <YAxis />
              <Tooltip formatter={tooltipFormatter} />
              <Legend formatter={legendFormatter} />
              <Bar dataKey="goal" fill={BAR_COLOR.goal} barSize={BAR_SIZE} />
              <Bar
                dataKey="intarget"
                fill={BAR_COLOR.intarget}
                barSize={BAR_SIZE}
              />
              <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
            </BarChart>
          </ResponsiveContainer>
        </Typography>
      </Grid>
    );
  };

  const renderAssistChart = () => {
    const data = Object.getOwnPropertyNames(matrix.assists).reduce(
      (result: any[], property) => {
        const playerName = matrix.assists[property][0].toName;
        if (!playerName) {
          return result;
        }
        result.push({
          name: playerName,
          total: matrix.assists[property].length,
        });
        return result;
      },
      []
    );
    if (data.length <= 0) {
      return null;
    }
    return (
      <Grid item xs={12} md={6} lg={6}>
        <Typography variant="h4">
          {intl.get("MATCH_TEAM_OVERVIEW_LAST_PASS_CHART_TITLE")}
          <Box mt={2} />
          <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis dataKey="name" angle={-45} interval={0} />
              <YAxis />
              <Tooltip />
              <Legend formatter={assistLegendFormatter} />
              <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
            </BarChart>
          </ResponsiveContainer>
        </Typography>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_TOTAL")}: ${
            summary.all.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.all.inTarget
          } (${calculatePecentage(
            summary.all.inTarget,
            summary.all.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.all.goal} (${calculatePecentage(
              summary.all.goal,
              summary.all.inTarget
            )} / ${calculatePecentage(summary.all.goal, summary.all.total)})`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_SHOOT_IN_PENALTY_TOTAL"
          )}: ${summary.inPenalty.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.inPenalty.inTarget
          } (${calculatePecentage(
            summary.inPenalty.inTarget,
            summary.inPenalty.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.inPenalty.goal} (${calculatePecentage(
              summary.inPenalty.goal,
              summary.inPenalty.inTarget
            )} / ${calculatePecentage(
              summary.inPenalty.goal,
              summary.inPenalty.total
            )}%)`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_SHOOT_OUT_PENALTY_TOTAL"
          )}: ${summary.outPenalty.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.outPenalty.inTarget
          } (${calculatePecentage(
            summary.outPenalty.inTarget,
            summary.outPenalty.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.outPenalty.goal} (${calculatePecentage(
              summary.outPenalty.goal,
              summary.outPenalty.inTarget
            )} / ${calculatePecentage(
              summary.outPenalty.goal,
              summary.outPenalty.total
            )})`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatisticsBoard data={dataArray} />
        </Grid>
      </Grid>
      <Box mt={2} />
      <Grid container spacing={2}>
        {renderShootChart()}
        {renderAssistChart()}
      </Grid>
    </>
  );
};
